// src/modules/myvault/components/automations/actions/CreateRecordAction.tsx

// What? CREATE_RECORD action editor: view picker + field→value mapper that
//       accepts literal text or `{{field}}` refs pulled from the triggering
//       row's data.
// Why?  Backend dispatcher expects `{view_id, data: {field: value}}`; a
//       typed form keeps admins from handcrafting JSON.
// How?  Loads workspace views via useViews, lets the user pick one, then
//       shows a row-editor for the `data` map with add/remove entries.

import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useViews } from "../../../hooks/useViews";
import type { ActionEditorProps } from "../types";

const CreateRecordAction: React.FC<ActionEditorProps> = ({
  config,
  onChange,
  context,
}) => {
  const { views } = useViews(context.workspaceId);
  const viewId = typeof config.view_id === "string" ? config.view_id : "";
  const data: Record<string, string> =
    typeof config.data === "object" && config.data !== null
      ? (config.data as Record<string, string>)
      : {};

  const [newField, setNewField] = useState("");
  const [newValue, setNewValue] = useState("");

  const updateField = (field: string, value: string) => {
    const next = { ...data };
    if (value === "") {
      delete next[field];
    } else {
      next[field] = value;
    }
    onChange({ data: next });
  };

  const removeField = (field: string) => {
    const next = { ...data };
    delete next[field];
    onChange({ data: next });
  };

  const handleAdd = () => {
    const key = newField.trim();
    if (!key || Object.prototype.hasOwnProperty.call(data, key)) return;
    onChange({ data: { ...data, [key]: newValue } });
    setNewField("");
    setNewValue("");
  };

  return (
    <Stack spacing={2}>
      <FormControl>
        <FormLabel>Target view</FormLabel>
        <Select
          size="sm"
          value={viewId}
          onChange={(_, val) => onChange({ view_id: (val as string) || "" })}
          placeholder="Select a view"
        >
          <Option value="">None</Option>
          {views.map((v) => (
            <Option key={v.id} value={String(v.id)}>
              {v.name}
            </Option>
          ))}
        </Select>
        <FormHelperText>
          New rows are created through the selected view. The backend validates
          field names against the view's schema at run time.
        </FormHelperText>
      </FormControl>

      <Box>
        <FormLabel>Field values</FormLabel>
        <FormHelperText>
          Accepts literal values or <code>{"{{field}}"}</code> references to
          fields on the triggering row.
        </FormHelperText>
        <Sheet variant="outlined" sx={{ borderRadius: "sm", p: 1, mt: 1 }}>
          <Stack spacing={1}>
            {Object.entries(data).length === 0 && (
              <Typography
                level="body-xs"
                sx={{ color: "text.tertiary", textAlign: "center", py: 1 }}
              >
                No field values configured.
              </Typography>
            )}
            {Object.entries(data).map(([field, value]) => (
              <Stack
                key={field}
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <Input
                  size="sm"
                  value={field}
                  disabled
                  sx={{ width: 200, fontFamily: "monospace" }}
                />
                <Input
                  size="sm"
                  value={value}
                  onChange={(e) => updateField(field, e.target.value)}
                  placeholder="{{ source_field }}"
                  sx={{ flex: 1 }}
                />
                <IconButton
                  size="sm"
                  variant="plain"
                  color="danger"
                  onClick={() => removeField(field)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Stack direction="row" spacing={1} alignItems="center">
              <Input
                size="sm"
                value={newField}
                onChange={(e) => setNewField(e.target.value)}
                placeholder="field_name"
                sx={{ width: 200, fontFamily: "monospace" }}
              />
              <Input
                size="sm"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder="literal or {{ref}}"
                sx={{ flex: 1 }}
              />
              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                startDecorator={<AddIcon />}
                onClick={handleAdd}
                disabled={
                  !newField.trim() ||
                  Object.prototype.hasOwnProperty.call(data, newField.trim())
                }
              >
                Add
              </Button>
            </Stack>
          </Stack>
        </Sheet>
      </Box>
    </Stack>
  );
};

export default CreateRecordAction;
